import React, { useEffect, useState } from "react";
import {
  Button,
  FormFieldSet,
  Modal,
  LogoLoader, Backdrop,
  TableAdv
} from "../../../../../components";
import { useNavigate, useLocation } from "react-router-dom";
import { mapTableRows } from "../../../../../utils";
import {
  fetchCompanies,
  mergeCompanyApi,
} from "../../../../../redux/slices/leads/leadsApi";
import { showToast } from "../../../../../components";
import { formatWords2 } from "../../../../../utils/stringUtils";

const MergeLeads = () => {
  const [deleteModal, setDeleteModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [fields, setFields] = useState([]);
  const [companyChange, setCompanyChange] = useState({});
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { selectedLeads } = location.state || {};

  useEffect(() => {
    const getCompanies = async () => {
      setLoading(true);

      try {
        const response = await fetchCompanies({
          limit: 10,
          page: 1,
          filters: {
            uiid: { type: "IN", value: selectedLeads },
          },
          ret: "uiid name website",
          ref: "JobsWithProspect",
          incl: ["uiid title companyId"],
          sort: null,
          postActions: null,
        });

        const companiesData = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

        //  Set companies only if data exists
        if (companiesData.length > 0) {
          setCompanies(companiesData);
        } else {
          setCompanies([]); // fallback
        }


        const allFields = [];

        if (companiesData.length > 0) {
          companiesData.forEach((company) => {
            const jobs = Array.isArray(company.jobIds) ? company.jobIds : [];

            jobs.forEach((job, idx) => {
              const prospect =
                Array.isArray(job?.prospectIds) && job.prospectIds.length > 0
                  ? job.prospectIds[0]
                  : {};

              allFields.push({
                key: `field_${allFields.length}`,
                title: job?.title || `Job ${idx + 1}`,
                email: prospect?.email?.email || prospect?.email || "",
                firstName: prospect?.firstName || "",
                lastName: prospect?.lastName || "",
                designation: prospect?.designation || "",
                jobUiid: job?.uiid || null,
                companyId: job?.companyId || null,
              });
            });
          });
        }

        // Set fields only if jobs/prospects exist
        if (allFields.length > 0) {
          setFields(allFields);
        } else {
          setFields([]); // fallback
        }

        setCompanyChange(
          allFields.reduce((acc, f) => {
            if (f.companyId) acc[f.key] = String(f.companyId);
            return acc;
          }, {})
        );
      } catch (err) {
        console.error("Error fetching companies:", err);

        // fallback on error
        setCompanies([]);
        setFields([]);
      } finally {
        setLoading(false);
      }
    };

    getCompanies();
  }, [selectedLeads]);

  const handleCompanyChange = (selectedCompanyUiid, rowKey) => {
    setCompanyChange((prev) => {
      const updated = { ...prev, [rowKey]: selectedCompanyUiid };

      if (rowKey === "master_record") {
        fields.forEach((f) => (updated[f.key] = selectedCompanyUiid));
      }

      return updated;
    });
  };

  const columns = [
    {
      key: "field",
      label: "Fields",
      render: (row) => row.field,
    },
    ...companies.map((company, colIndex) => ({
      key: `company_${colIndex}`,
      label: formatWords2(company.name || `Company ${colIndex + 1}`),
      render: (row) => {
        const rowKey = row.key;
        const selectedForRow = companyChange[rowKey] || "";

        return (
          <FormFieldSet
            type="radio"
            noTitle
            name={`row_${rowKey}`}
            value={selectedForRow}
            size="sm"
            options={[{ label: "", value: String(company.uiid) }]}
            onChange={(e) => handleCompanyChange(e.target.value, rowKey)}
          />
        );
      },
    })),
  ];

  const rows = fields.length
    ? [
      {
        field: (
          <div className="flex flex-col py-1 leading-tight">
            <span className="text-sm font-semibold text-gray-900">
              Master Record
            </span>
            <span className="text-[12px] text-gray-600">
              Select the master lead where other data will merge
            </span>
          </div>
        ),
        key: "master_record",
      },
      ...fields.map((f) => ({
        field: (
          <div className="flex flex-col py-1 leading-tight">
            <span className="text-sm font-medium text-gray-900">
              {formatWords2(f.title)}
            </span>
            {(f.firstName || f.lastName || f.designation) && (
              <span className="text-[12px] text-gray-600">
                {[formatWords2(f.firstName), formatWords2(f.lastName)].filter(Boolean).join(" ")}
                {f.designation ? ` (${formatWords2(f.designation)})` : ""}
              </span>
            )}
            {f.email && (
              <span className="text-[12px] text-blue-600 underline">
                {f.email}
              </span>
            )}
          </div>
        ),
        key: f.key,
      })),
    ]
    : [];


  const validRows = rows.filter((r) => r.key && r.field);
  const mappedRows = mapTableRows(validRows, columns);

  const createMergePayload = () => {
    const masterCompanyUiid = companyChange["master_record"];
    if (!masterCompanyUiid) return;

    const jobIds = fields.map((f) => f.jobUiid).filter(Boolean);
    const deleteCompanyIds = companies
      .map((c) => c.uiid)
      .filter((uiid) => String(uiid) !== String(masterCompanyUiid));

    const masterCompany = companies.find(
      (c) => String(c.uiid) === String(masterCompanyUiid)
    );

    return {
      coll: "Company",
      payload: {
        mergeCompanyId: Number(masterCompanyUiid),
        updatedData: {
          name: masterCompany?.name || "",
          status: 2,
          jobIds: jobIds,
        },
        deleteCompanyIds: deleteCompanyIds,
      },
    };
  };



  const handleConfirmMerge = async () => {
    const payload = createMergePayload();
    if (!payload) return;
    setLoading(true);

    try {
      const response = await mergeCompanyApi({ payload });

      if (response?.data) {
        showToast("Company merged successfully!", "success", 2000);
        setDeleteModal(false);
        navigate("/ats/leads#all-leads");
      } else {
        showToast("Failed to merge company!", "error", 2000);
      }
    } catch (error) {
      console.error("Merge API error:", error);
      showToast("Something went wrong!", "error", 2000);
    } finally {
      setLoading(false); // always stop loader
    }
  };

  return (
    <div className="p-2">
      <Button
        title={"Merge Lead"}
        icon={"west"}
        color="black"
        variant="text"
        onClick={() => navigate("/ats/leads?leads=all-leads")}
        className="font-[500] !px-0"
      />

      <p className="text-sm text-neutrals-600">
        Note: Lead selected as master record will be the record saved with other
        details. You can choose from the following fields for migrating data
        into master record.
      </p>

      <div>
        <TableAdv
          tableId={"ats_leads_mergeleads_table"}
          columns={columns}
          rows={mappedRows}
          toolbarRight={{
            fullScreen: true,
          }}
          tdClassName="!py-0.5"
        />
      </div>

      <div className="flex justify-end gap-4 m-4">
        <Button title={"Cancel"} variant="outlined" size="sm" />
        <Button
          title={"Merge"}
          size="sm"
          onClick={() => setDeleteModal(true)}
          disabled={!companyChange["master_record"]}
        />
      </div>

      <Modal isOpen={deleteModal} showCloseButton={false}>
        <div className="flex flex-col justify-center items-center gap-2 py-8 px-4">
          <span className="material-symbols-outlined !text-7xl !text-warning">
            info
          </span>
          <h5 className="font-semibold">Are You Sure?</h5>
          <p className="text-xs text-center">
            Once merged, only the master record will remain. Selected
            information will be captured, and others will be deleted.
          </p>
          <div className="flex gap-4 my-2">
            <Button
              title={"Cancel"}
              variant="outlined"
              color="black"
              size="sm"
              onClick={() => setDeleteModal(false)}
            />
            <Button title={"Confirm"} size="sm" onClick={handleConfirmMerge} />
          </div>
        </div>
      </Modal>
      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default MergeLeads;
